import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { DefaultLocalProductService } from '@asm/features/sync/services/product/default-local-product.service';

/**
 * Handler offline para endpoints de produtos.
 * Cobre: GET /search/quick-order, GET /product/{code}/similar
 *
 * Lógica de filtragem delegada a OfflineProductFilterService.
 */
@Injectable({ providedIn: 'root' })
export class ProductOfflineHandler {
  private readonly productRepo = inject(ProductRepository);
  private readonly productService = inject(DefaultLocalProductService);

  canHandle(req: HttpRequest<unknown>): boolean {
    const { url, method } = req;
    return method === 'GET' && (url.includes('/search/quick-order') || /\/product\/[^/]+\/similar/.test(url));
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url, method } = req;

    if (method === 'GET' && url.includes('/search/quick-order')) {
      return this.handleProductSearch(req);
    }

    if (method === 'GET' && /\/product\/[^/]+\/similar/.test(url)) {
      return new HttpResponse({ status: 200, body: [] });
    }

    throw new Error(`[ProductOfflineHandler] Rota não mapeada: ${method} ${url}`);
  }

  private async handleProductSearch(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const allProducts = await firstValueFrom(this.productRepo.getAll());
    const query = req.params.get('query') ?? '';
    const page = Number(req.params.get('page') ?? '0');
    const limit = Number(req.params.get('limit') ?? '50');
    const filtered = this.productService.filterProducts(allProducts, query);
    const start = page * limit;
    const paginated = filtered.slice(start, start + limit);

    return new HttpResponse({
      status: 200,
      body: {
        products: paginated,
        totalProducts: filtered.length,
        totalPages: Math.ceil(filtered.length / limit),
        currentPage: String(page),
      },
    });
  }
}
